import React, { FC } from 'react'

import {
  calculatePriceWithVAT,
  HELP_MARITIME_NETWORKS,
} from '@smarty-nx/code-utils'
import { Typography } from '@smarty-nx/ds'

import { CONFIG } from '../../config'
import { MARITIME } from '../../constants/globalStrings'
import WifiSvg from '../../static/images/wifi.svg'
import ColorButton from '../ColorButton'
import {
  ButtonContainer,
  Card,
  Container,
  RoamingHeading,
  RoamingParagraph,
  Table,
  TdPricing,
  WifiCard,
} from '../shared'
import classes from '../shared/roaming.module.scss'

interface NotAvailableProps {
  display_country?: string
  iso_country_name_slug?: string
}

export const NotAvailable: FC<NotAvailableProps> = ({ display_country }) => (
  <React.Fragment>
    <RoamingHeading>
      Using SMARTY in {display_country} is not available at the moment.
    </RoamingHeading>
    <Typography variant="Kollektif/Small">
      We&apos;re not able to support using your SMARTY SIM in {display_country}{' '}
      at the moment. We recommend buying a local SIM or using local Wifi.
    </Typography>
    <ButtonContainer>
      <ColorButton to="/roaming/international" color="#EFC873">
        Select another country
      </ColorButton>
    </ButtonContainer>
  </React.Fragment>
)

interface PriceTableProps {
  display_country?: string
  iso_country_name_slug?: string
  data?: number
  back_to_uk?: number
  back_to_uk_mobile?: number
  back_to_uk_other?: number
  local_roaming_fixed?: number
  local_roaming_mobile?: number
  local_roaming_other?: number
  sms_rsn1_rseu?: number
  inbound_calls?: number
  mms_picture_rmn1?: number
}

export const PriceTable: FC<PriceTableProps> = ({
  display_country,
  iso_country_name_slug,
  data,
  back_to_uk,
  back_to_uk_mobile,
  back_to_uk_other,
  local_roaming_fixed,
  local_roaming_mobile,
  local_roaming_other,
  sms_rsn1_rseu,
  inbound_calls,
  mms_picture_rmn1,
}) => {
  const isMaritime = iso_country_name_slug === MARITIME

  return (
    <>
      <RoamingHeading>
        Using SMARTY {isMaritime ? 'on' : 'in'} {display_country}
      </RoamingHeading>
      {isMaritime && (
        <Typography variant="Kollektif/Medium" className={classes.subtext}>
          Calls, texts and data used while connected to maritime networks
          aren&apos;t included in your plan or roaming allowances.
        </Typography>
      )}
      <Container bgColor="#ffd67c" padding="15px">
        <Table>
          <thead>
            <tr>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {isMaritime ? (
              <>
                <tr>
                  <td>Using Internet or Data</td>
                  <TdPricing>
                    {calculatePriceWithVAT(data)}
                    <span>/ mb</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Calls to UK/EU/Outside EU Landlines</td>
                  <TdPricing>
                    {calculatePriceWithVAT(back_to_uk)}
                    <span>/ min</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Calls to UK/EU/Outside EU Mobiles</td>
                  <TdPricing>
                    {calculatePriceWithVAT(back_to_uk_mobile)}
                    <span>/ min</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Calls to UK/EU/Outside EU Other</td>
                  <TdPricing>
                    {calculatePriceWithVAT(back_to_uk_other)}
                    <span>/ min</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>SMS to any numbers</td>
                  <TdPricing>
                    {calculatePriceWithVAT(sms_rsn1_rseu)}
                    <span>/ sms</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Receiving calls from any number</td>
                  <TdPricing>
                    {calculatePriceWithVAT(inbound_calls)}
                    <span>/ min</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Receiving texts from any number</td>
                  <TdPricing>FREE</TdPricing>
                </tr>

                <tr>
                  <td>MMS to any numbers</td>
                  <TdPricing>
                    {calculatePriceWithVAT(mms_picture_rmn1)}
                    <span>/ mms</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Calls to Maritime & Satellite networks</td>
                  <TdPricing>
                    {calculatePriceWithVAT(local_roaming_fixed)}
                    <span>/ min</span>
                  </TdPricing>
                </tr>
              </>
            ) : (
              <>
                <tr>
                  {data ? (
                    <>
                      <td>Using Internet or Data</td>
                      <TdPricing>
                        {calculatePriceWithVAT(data)}
                        <span>/ mb</span>
                      </TdPricing>
                    </>
                  ) : (
                    <>
                      <td>Internet and Data Unavailable</td>
                      <td />
                    </>
                  )}
                </tr>

                <tr>
                  <td>Call to UK Landlines</td>
                  <TdPricing>
                    {calculatePriceWithVAT(back_to_uk)}
                    <span>/ min</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Call to UK Mobiles</td>
                  <TdPricing>
                    {calculatePriceWithVAT(back_to_uk_mobile)}
                    <span>/ min</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Call to UK Other</td>
                  <TdPricing>
                    {calculatePriceWithVAT(back_to_uk_other)}
                    <span>/ min</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Call to {display_country} Landlines</td>
                  <TdPricing>
                    {calculatePriceWithVAT(local_roaming_fixed)}
                    <span>/ min</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Call to {display_country} Mobiles</td>
                  <TdPricing>
                    {calculatePriceWithVAT(local_roaming_mobile)}
                    <span>/ min</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Call to {display_country} Other</td>
                  <TdPricing>
                    {calculatePriceWithVAT(local_roaming_other)}
                    <span>/ min</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>SMS to {display_country} numbers</td>
                  <TdPricing>
                    {calculatePriceWithVAT(sms_rsn1_rseu)}
                    <span>/ sms</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Receiving calls from any number</td>
                  <TdPricing>
                    {calculatePriceWithVAT(inbound_calls)}
                    <span>/ min</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Receiving texts from any number</td>
                  <TdPricing>FREE</TdPricing>
                </tr>

                <tr>
                  <td>MMS to UK numbers</td>
                  <TdPricing>
                    {calculatePriceWithVAT(mms_picture_rmn1)}
                    <span>/ mms</span>
                  </TdPricing>
                </tr>

                <tr>
                  <td>Call and text other numbers</td>
                  <TdPricing>
                    <span>
                      <a
                        href="/priceguide"
                        rel="noopener noreferrer"
                        target="_blank"
                        aria-label="See all prices .PDF (253KB) link opens in a new window"
                      >
                        See all prices
                      </a>
                    </span>
                  </TdPricing>
                </tr>
              </>
            )}
          </tbody>
        </Table>
        <Card className={classes.informationCard}>
          <Typography
            component="h2"
            variant="Kollektif/MediumEmphasis"
            className={classes.cardHeadingYellow}
          >
            Before you go
          </Typography>
          <Typography component="h2" variant="Kollektif/MediumEmphasis">
            Phone settings update required
          </Typography>
          <Typography variant="Kollektif/Small">
            Most customers can use their SMARTY SIM{' '}
            {isMaritime ? 'on maritime networks' : 'abroad'} without changing
            any phone settings. If your phone doesn&apos;t connect while
            you&apos;re away, we&apos;ve created step-by-step guides to help get
            you up and running.
          </Typography>
          <a href="/roaming-setup" className={classes.setupLink}>
            Set up your phone now
          </a>
          <Typography component="h2" variant="Kollektif/MediumEmphasis">
            Buy &quot;Out of plan&quot; add-ons
          </Typography>
          {isMaritime ? (
            <>
              <Typography variant="Kollektif/Small" className={classes.subtext}>
                Calls, texts and data used on maritime networks are paid for
                using your &quot;Out of plan&quot; add-ons.
              </Typography>
              <Typography variant="Kollektif/Small">
                If you don&apos;t have enough add-ons, you can choose to put
                that money towards your next month or use it next time you need
                it.
              </Typography>
            </>
          ) : (
            <Typography variant="Kollektif/Small">
              Data, calls or texts outside the EU won&apos;t work if you
              don&apos;t have some &quot;Out of plan&quot; add-ons in your
              account. If you don&apos;t use all your &quot;Out of plan&quot;
              add-ons, you can choose to put that money towards your next month
              or you can use it next time you need it.
            </Typography>
          )}
          <a
            href={`${CONFIG.DASHBOARD_URL}cash-addons`}
            className={classes.setupLink}
          >
            Buy &quot;Out of plan&quot; add-ons
          </a>
          {isMaritime && (
            <>
              <Typography component="h2" variant="Kollektif/MediumEmphasis">
                Want to know more about maritime networks?
              </Typography>
              <a
                href={HELP_MARITIME_NETWORKS}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.setupLink}
              >
                Find out here
              </a>
            </>
          )}
        </Card>
      </Container>
      <WifiCard>
        <div>
          <Typography component="h2" variant="Kollektif/MediumEmphasis">
            Save money by connecting to wifi
          </Typography>
          <Typography variant="Kollektif/Small">
            Using your phone abroad is expensive. We always try and give you a
            good deal, but have less control over pricing in other countries.
          </Typography>
          <Typography variant="Kollektif/Small">
            To save money you should connect to wifi wherever possible.
          </Typography>
        </div>
        <img src={WifiSvg} alt="Wifi" loading="lazy" decoding="async" />
      </WifiCard>
      <RoamingParagraph>
        Prices are shown including VAT where applicable.
      </RoamingParagraph>
    </>
  )
}
